import { cssVariables, Icon } from "@/components";
import { Divider } from "@/components/divider";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { FeeLine } from "@/offering/api";
import React, { useState } from "react";
import { Modal, OverlayTrigger, Popover } from "react-bootstrap";

interface AdditionalFeesItemProps {
  feeLines: FeeLine[];
  currency: string | null;
  display?: "list" | "modal";
}

export function AdditionalFeesItem({
  feeLines,
  currency,
  display = "modal",
}: AdditionalFeesItemProps) {
  const { fbt } = useI18n();

  const [feeDialogOpen, setFeeDialogOpen] = useState(false);

  if (feeLines.length === 0) {
    return null;
  }

  const renderPopover = (dislayInfo: string) => (
    <span>
      <OverlayTrigger
        placement="auto"
        overlay={
          <Popover
            style={{
              backgroundColor: "white",
              borderColor: cssVariables.borderColorDefault,
              borderWidth: 1,
              borderRadius: cssVariables.radius,
              boxShadow: cssVariables.shadow,
              padding: "16px",
              minWidth: "264px",
              color: "#82808e",
            }}
          >
            <span>{fbt(dislayInfo)}</span>
          </Popover>
        }
      >
        <span>
          <Icon name="info" size={16} />
        </span>
      </OverlayTrigger>
    </span>
  );

  if (feeLines.length === 1) {
    const feeLine = feeLines[0];
    return (
      <div className="row g-0">
        <span className="col-8 col-sm-8 col-md-8 d-flex gap-1">
          <span>{fbt(feeLine.displayName)}</span>
          {!!feeLine.displayInformation &&
            renderPopover(feeLine.displayInformation)}
        </span>
        <span className="col-4 col-sm-4 col-md-4 text-end">
          {fbt.formatCurrency(feeLine.calculatedAmount, currency)}
        </span>
      </div>
    );
  }

  if (display === "list") {
    return (
      <div>
        {feeLines.map((feeLine) => (
          <div key={feeLine.id} className="row g-0 mb-2">
            <span className="col-8 col-sm-8 col-md-8 d-flex gap-1">
              <span>{feeLine.displayName}</span>
              {!!feeLine.displayInformation &&
                renderPopover(feeLine.displayInformation)}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-end">
              {fbt.formatCurrency(feeLine.calculatedAmount, currency)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const total = feeLines.reduce(
    (prev, current) => prev + current.calculatedAmount,
    0
  );

  return (
    <div>
      <div className="row g-0">
        <span
          role="button"
          className="col-8 col-sm-8 col-md-8 text-decoration-underline"
          onClick={() => setFeeDialogOpen(true)}
        >
          {fbt("Additional fees")}
        </span>
        <span className="col-4 col-sm-4 col-md-4 text-end">
          {fbt.formatCurrency(total, currency)}
        </span>
      </div>
      <Modal
        show={feeDialogOpen}
        onHide={() => setFeeDialogOpen(false)}
        centered
        className="header-center"
      >
        <div>
          <Modal.Header closeButton>
            <Modal.Title as="h5">{fbt("Additional fees")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {feeLines.map((feeLine) => (
                <div key={feeLine.id} className="row g-0 mb-2">
                  <span className="col-8 col-sm-8 col-md-8 d-flex gap-1">
                    <span>{feeLine.displayName}</span>
                    {!!feeLine.displayInformation &&
                      renderPopover(feeLine.displayInformation)}
                  </span>
                  <span className="col-4 col-sm-4 col-md-4 text-end">
                    {fbt.formatCurrency(feeLine.calculatedAmount, currency)}
                  </span>
                </div>
              ))}
              <Divider />
              <div className="row fw-bold mt-2">
                <span className="col-8">{fbt("Total")}</span>
                <span className="col-4 text-end">
                  {fbt.formatCurrency(Math.abs(total), currency)}
                </span>
              </div>
            </div>
          </Modal.Body>
        </div>
      </Modal>
    </div>
  );
}
